<template>
  <span>
    <template v-for="(segment, index) in textSegments" :key="index">
      <span v-if="segment.highlighted" :class="highlightClass">{{
        segment.text
      }}</span>
      <template v-else>{{ segment.text }}</template>
    </template>
  </span>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  text: { type: String, required: true },
  highlights: { type: [String, Array], default: null },
  highlightClass: { type: String, default: 'font-semibold text-primary-600' },
})

const textSegments = computed(() => {
  if (!props.text || !props.highlights)
    return [{ text: props.text, highlighted: false }]

  // Process highlights to remove duplicates and sort them
  const highlightRanges = new Set()

  // Handle both string format and array format
  if (typeof props.highlights === 'string') {
    props.highlights
      .split(';')
      .filter(Boolean)
      .forEach((highlight) => {
        highlight.split(',').forEach((range) => {
          highlightRanges.add(range)
        })
      })
  } else if (Array.isArray(props.highlights)) {
    props.highlights.forEach((range) => highlightRanges.add(range))
  }

  // Convert to array of start-end objects and sort by start position
  const highlightArray = Array.from(highlightRanges)
    .map((range) => {
      const [start, end] = range.split('-').map(Number)
      return { start, end: end !== undefined ? end : start }
    })
    .sort((a, b) => a.start - b.start)

  // Merge overlapping highlights
  const mergedHighlights = []
  if (highlightArray.length > 0) {
    let current = highlightArray[0]

    for (let i = 1; i < highlightArray.length; i++) {
      if (highlightArray[i].start <= current.end + 1) {
        // Merge overlapping or adjacent ranges
        current.end = Math.max(current.end, highlightArray[i].end)
      } else {
        mergedHighlights.push(current)
        current = highlightArray[i]
      }
    }
    mergedHighlights.push(current)
  }

  // Create text segments
  const segments = []
  let lastIndex = 0

  mergedHighlights.forEach(({ start, end }) => {
    // Add non-highlighted text
    if (start > lastIndex) {
      segments.push({
        text: props.text.substring(lastIndex, start),
        highlighted: false,
      })
    }

    // Add highlighted text
    segments.push({
      text: props.text.substring(start, end + 1),
      highlighted: true,
    })

    lastIndex = end + 1
  })

  // Add any remaining text
  if (lastIndex < props.text.length) {
    segments.push({
      text: props.text.substring(lastIndex),
      highlighted: false,
    })
  }

  return segments
})
</script>
